import React, { useMemo, useCallback, useState, useContext, useRef, useEffect } from 'react';
import { AutoComplete } from 'antd';
import type { AutoCompleteProps as AntAutoCompleteProps } from 'antd';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { parseChangeAction, createCommand } from '../../base/actionParser';
import { useApiService } from '../../base/hooks/useApiService';
import { useFieldValidation } from '../../base/useFieldValidation';
import { ValidateHelp } from '../../base/ValidateHelp';
import type { BaseComponentProps, DataSetConfig, ValidationDisplayConfig } from '../../types/common';
import { DataSetInheritContext } from '../../base/DataSetInheritContext';
import { extractApiResponseList } from '../../base/utils/extractApiResponseList';

const VALIDATE_DEBOUNCE_MS = 200;

// 远程选项配置接口
interface RemoteOptionsConfig {
  queryApi?: string;
  keyWordParamName?: string;
  valueKey?: string;
  labelKey?: string;
  fixedParams?: any[];
  /**
   * 响应中选项数组所在字段名（与 AndSelect optionsCfg.field 同义）。
   * 如 "list"；留空时根为数组则直接用，否则取 list 或 records。
   */
  field?: string;
  /** 为 true 时：从下拉选中后，dataSetField 写入 label（展示字段），而非 valueKey */
  bindLabelOnSelect?: boolean;
}

export interface AndAutoCompleteProps
  extends Omit<AntAutoCompleteProps, 'onChange' | 'onSelect'>,
    BaseComponentProps,
    DataSetConfig,
    ValidationDisplayConfig {
  changeAction?: string;
  selectAction?: string;
  onChange?: (value: string) => void;
  onSelect?: (value: string, option: any) => void;
  /** 远程数据URL（自定义属性，不传递给AutoComplete） */
  remoteUrl?: string;
  /** 远程选项配置 */
  remoteOptions?: RemoteOptionsConfig;
  /** 搜索动作（自定义属性，不传递给AutoComplete） */
  searchAction?: string;
  rules?: Array<{
    type?: string;
    required?: boolean;
    message?: string;
    pattern?: string | RegExp;
    min?: number;
    max?: number;
  }>;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
}

function resolveLabelFromOption(option: any, labelKey: string): string {
  if (!option) return '';
  if (option.label != null && option.label !== '') return String(option.label);
  if (labelKey && option[labelKey] != null) return String(option[labelKey]);
  return '';
}

/** 将 Ant Design / API 选项转为可入队的纯对象，避免 Immer circular references */
function toPlainOptionPayload(option: any, valueKey: string, labelKey: string): Record<string, unknown> {
  if (!option || typeof option !== 'object') return {};
  const source = option.data && typeof option.data === 'object' ? option.data : option;
  const out: Record<string, unknown> = {};
  const copyKeys = new Set<string>([
    'id',
    valueKey,
    labelKey,
    'value',
    'label',
    'patientId',
    'patientName',
    'patientNo',
    'patientGender',
    'patientAge',
  ]);
  for (const key of copyKeys) {
    if (key && source[key] !== undefined && source[key] !== null) {
      out[key] = source[key];
    }
  }
  if (out.value === undefined && source[valueKey] !== undefined) {
    out.value = source[valueKey];
  }
  if (out.label === undefined && source[labelKey] !== undefined) {
    out.label = source[labelKey];
  }
  if (out.id === undefined && out.value !== undefined && valueKey === 'id') {
    out.id = out.value;
  }
  return out;
}

function mapRemoteOptionItem(
  item: Record<string, unknown>,
  valueKey: string,
  labelKey: string,
): Record<string, unknown> {
  const value = item[valueKey];
  const label = item[labelKey] ?? value;
  return {
    ...item,
    value,
    label,
  };
}

export const AndAutoComplete: React.FC<AndAutoCompleteProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  changeAction,
  selectAction,
  onChange,
  onSelect,
  value: propValue,
  defaultValue,
  options,
  rules,
  validateOnBlur,
  validateOnChange,
  showValidateHelp = true,
  // 解构自定义属性，防止传递到 DOM
  remoteUrl: _remoteUrl,
  remoteOptions,
  searchAction,
  ...restProps
}) => {
  const ctx = useContext(DataSetInheritContext);
  const addCommand = useAndStore((state) => state.addCommand);
  const setDataSet = useAndStore((state) => state.setDataSet);
  const validateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );

  const valueKey = remoteOptions?.valueKey ?? 'value';
  const labelKey = remoteOptions?.labelKey ?? 'label';
  const bindLabelOnSelect = useMemo(() => {
    if (remoteOptions?.bindLabelOnSelect !== undefined) {
      return remoteOptions.bindLabelOnSelect;
    }
    return valueKey !== labelKey;
  }, [remoteOptions?.bindLabelOnSelect, valueKey, labelKey]);

  const selectingFromListRef = useRef(false);
  const selectedOptionRef = useRef<any>(null);

  // 状态
  const [internalOptions, setInternalOptions] = useState<any[]>(options || []);
  const [, setLoading] = useState(false);
  const searchTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);

  // API 服务
  const { get: apiGet } = useApiService();

  const { validate, help } = useFieldValidation({
    rules,
    dataSet,
    dataSetField,
    sessionId,
    id,
    develop,
  });

  const storeValue = useAndStore((s) =>
    dataSetKey && dataSetField ? s.dataSets[dataSetKey]?.[dataSetField] : undefined,
  );

  const displayValue = useMemo(() => storeValue ?? propValue ?? defaultValue, [storeValue, propValue, defaultValue]);

  const scheduleValidate = useCallback(
    (valueToValidate: unknown) => {
      if (!validateOnChange) return;
      if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
      validateTimerRef.current = setTimeout(() => {
        validateTimerRef.current = null;
        validate(valueToValidate);
      }, VALIDATE_DEBOUNCE_MS);
    },
    [validateOnChange, validate],
  );

  // 处理搜索
  const handleSearch = useCallback((searchText: string) => {
    if (searchAction && sessionId) {
      addCommand(sessionId, createCommand(searchAction, { value: searchText, dataSet, dataSetField }));
    }

    // 如果没有配置 queryApi，只做本地处理（Antd AutoComplete 默认支持）
    if (!remoteOptions?.queryApi) {
      if (options) {
          setInternalOptions(
             options.filter(opt =>
               (opt.value?.toString().toLowerCase().includes(searchText.toLowerCase()) ||
                opt.label?.toString().toLowerCase().includes(searchText.toLowerCase()))
             )
          );
      }
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      if (!remoteOptions?.queryApi) return;
      setLoading(true);
      try {
        const { queryApi, keyWordParamName = 'keyword', fixedParams = [] } = remoteOptions;

        // 构建参数
        const params: Record<string, any> = {};
        if (keyWordParamName) {
          params[keyWordParamName] = searchText;
        }

        // 处理固定参数
        if (Array.isArray(fixedParams)) {
             fixedParams.forEach(p => {
                 if(p.key) params[p.key] = p.value;
             });
        }

        const res = await apiGet(queryApi, params);

        const list = extractApiResponseList(res, remoteOptions.field) as Record<string, unknown>[];

        const mappedOptions = list.map((item) =>
          mapRemoteOptionItem(item, valueKey, labelKey),
        );

        setInternalOptions(mappedOptions);

      } catch (err) {
        console.error("Failed to fetch remote options:", err);
        setInternalOptions([]);
      } finally {
        setLoading(false);
      }
    }, 500); // Debounce 500ms

  }, [remoteOptions, options, apiGet, valueKey, labelKey, searchAction, sessionId, addCommand, dataSet, dataSetField]);

  // 当 options prop 变化时更新 internalOptions (如果未配置 remote)
  React.useEffect(() => {
      if (!remoteOptions?.queryApi && options) {
          setInternalOptions(options);
      }
  }, [options, remoteOptions?.queryApi]);


  const handleChange = useCallback((value: string) => {
    if (onChange) { onChange(value); return; }

    const isSelect = selectingFromListRef.current;
    const option = isSelect ? selectedOptionRef.current : undefined;
    const labelText = option ? resolveLabelFromOption(option, labelKey) : '';
    const plainOption = option ? toPlainOptionPayload(option, valueKey, labelKey) : undefined;
    const storedValue = isSelect && bindLabelOnSelect && labelText ? labelText : value;

    if (!(isSelect && bindLabelOnSelect)) {
      if (dataSetKey && dataSetField) setDataSet(dataSetKey, dataSetField, value);
    }

    if (changeAction && sessionId) {
      const changeValue = storedValue;
      const { actionName, arg } = parseChangeAction(changeAction, changeValue, { id, dataSet, dataSetField }, ctx);
      addCommand(sessionId, createCommand(actionName, {
        ...arg,
        source: isSelect ? 'select' : 'input',
        ...(isSelect ? { value, label: labelText || value, option: plainOption } : {}),
      }));
    }

    scheduleValidate(storedValue);
  }, [onChange, dataSetKey, dataSetField, setDataSet, changeAction, sessionId, id, addCommand, ctx, bindLabelOnSelect, labelKey, valueKey, scheduleValidate]);

  const handleSelect = useCallback((value: string, option: any) => {
    if (onSelect) { onSelect(value, option); return; }

    const plainOption = toPlainOptionPayload(option, valueKey, labelKey);
    selectingFromListRef.current = true;
    selectedOptionRef.current = plainOption;
    queueMicrotask(() => {
      selectingFromListRef.current = false;
      selectedOptionRef.current = null;
    });

    const labelText = resolveLabelFromOption(plainOption, labelKey);
    if (bindLabelOnSelect && dataSetKey && dataSetField && labelText) {
      setDataSet(dataSetKey, dataSetField, labelText);
    }

    if (selectAction && sessionId) {
      addCommand(sessionId, createCommand(selectAction, {
        value,
        label: labelText || value,
        option: plainOption,
        dataSet,
        dataSetField,
      }));
    }
  }, [onSelect, selectAction, sessionId, addCommand, bindLabelOnSelect, dataSetKey, dataSetField, setDataSet, labelKey, valueKey, dataSet, dataSetField]);

  const handleBlur = useCallback(() => {
    if (!validateOnBlur) return;
    validate(displayValue);
  }, [validateOnBlur, validate, displayValue]);

  useEffect(
    () => () => {
      if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
    },
    [],
  );

  return (
    <div style={{ width: '100%' }}>
      <AutoComplete
          {...restProps}
          disabled={develop ? false : restProps.disabled}
          open={develop ? false : undefined}
          options={internalOptions}
          value={displayValue}
          onChange={handleChange}
          onSelect={handleSelect}
          onSearch={handleSearch}
          onBlur={handleBlur}
          status={help ? ('error' as const) : undefined}
          style={{ width: '100%', ...restProps.style }}
      />
      <ValidateHelp help={help} showValidateHelp={showValidateHelp} />
    </div>
  );
};

export default AndAutoComplete;
